import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from '../utils/axiosConfig';
import { tokenManager } from '../utils/tokenManager';
import config from '../utils/config';
import { getActionErrorMessage } from '../utils/errorHandler';
import { useToast } from '../utils/toastContext';
import clinExpertLogo from "../ClinExpert_logo.png";
import "../styles/LoginForm.css";
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaStethoscope, FaUserGraduate, FaArrowRight } from "react-icons/fa";

const LoginForm = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { showError } = useToast();

  useEffect(() => {
    // Clear stale/partial auth state only when there is no valid session.
    // GuestRoute already redirects authenticated users away, so if we reach
    // this point the token is absent or expired — safe to wipe.
    const token = localStorage.getItem('access_token');
    const expiry = localStorage.getItem('token_expiry');
    const sessionValid = token && expiry && new Date() < new Date(expiry);
    if (!sessionValid) {
      localStorage.clear();
    }
  }, []);

  const handleLogin = async (event) => {
    event.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append("username", username);
      formData.append("password", password);

      const loginResponse = await axiosInstance.post("/auth/token", formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        timeout: config.AUTH_TIMEOUT, // Extended timeout for Render cold starts
      });

      const { access_token, role, isAdmin, status, time, ...otherData } = loginResponse.data;
      
      // Store token directly in localStorage (simplified for debugging)
      localStorage.setItem('access_token', access_token);
      localStorage.setItem("role", role);
      localStorage.setItem("isAdmin", isAdmin);
      if (status) {
        localStorage.setItem('student_status', status);
      }
      
      // Store user ID if available in the response
      if (otherData.user_id) {
        localStorage.setItem('user_id', otherData.user_id);
      }
      if (otherData.student_id) {
        localStorage.setItem('student_id', otherData.student_id);
      }
      if (otherData.preceptor_id) {
        localStorage.setItem('preceptor_id', otherData.preceptor_id);
      }
      if (otherData.admin_id) {
        localStorage.setItem('admin_id', otherData.admin_id);
      }
      
      // Initialize TokenManager after successful login
      tokenManager.setToken(access_token, time || 500);

      // Navigate based on role
      if (role === "student") {
        navigate("/StudentDashboard");
      } else if (role === "super_admin") {
        navigate("/SuperAdminDashboard");
      } else if (role === "admin") {
        navigate("/StandaloneAdminDashboard");
      } else if (role === "preceptor") {
        if (isAdmin) {
          navigate("/AdminDashboard");
        } else {
          navigate("/PreceptorDashboard");
        }
      } else {
        const roleError = "Unauthorized role.";
        setError(roleError);
        showError(roleError);
      }
    } catch (err) {
      // Use sanitized error message - never show raw backend errors
      const message = err.response?.status === 401
        ? "Invalid username or password. Please try again."
        : getActionErrorMessage(err, "login");
      setError(message);
      showError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-shell">
        <section className="login-brand-panel">
          <img src={clinExpertLogo} alt="ClinExpert Logo" className="logo dual-logo" />
          <h1 className="login-title">ClinExpert Portal</h1>
          <p className="login-subtitle">Purpose-built for clinical education management, scheduling, and assessment workflows.</p>
          <div className="login-feature-list">
            <div className="login-feature-item">
              <FaStethoscope />
              <span>Trusted by medical faculty</span>
            </div>
            <div className="login-feature-item">
              <FaUserGraduate />
              <span>Designed for students and preceptors</span>
            </div>
          </div>
          <p className="login-brand-footer">Brought to you by ClinExpert Inc</p>
        </section>

        <section className="login-form-panel content-box">
          <h2 className="login-form-title">Sign in securely</h2>
          <p className="login-form-caption">Use your institutional credentials to continue.</p>

          <form onSubmit={handleLogin}>
            <div className="input-group">
              <span className="input-icon">
                <FaEnvelope />
              </span>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Email or user ID"
                required
                autoComplete="username"
              />
            </div>

            <div className="input-group">
              <span className="input-icon">
                <FaLock />
              </span>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                className="input-icon input-eye"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={isLoading}
            >
              {isLoading ? "Authenticating..." : "Sign In"}
            </button>

            {error ? <p className="login-inline-status" role="status">{error}</p> : null}
          </form>

          <div className="login-footer-actions">
            <button
              type="button"
              className="join-org-link"
              onClick={() => navigate('/join-organization')}
            >
              New organization? Apply to join ClinExpert <FaArrowRight />
            </button>
            <button
              type="button"
              className="forgot-password-link"
              onClick={() => navigate('/forgot-password')}
            >
              Forgot Password?
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default LoginForm;
